import { useEffect, useState } from "react";
import { Phone, Menu, X } from "lucide-react";
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
    SheetClose,
} from "@/components/ui/sheet";
import { PHONE_DISPLAY, PHONE_HREF, LOGO_URL, BUSINESS_NAME } from "@/lib/contact";

const NAV = [
    { href: "/#bin-sizes", label: "Bin Sizes", slug: "bin-sizes" },
    { href: "/#what-we-take", label: "What We Take", slug: "what-we-take" },
    { href: "/#why-us", label: "Why Us", slug: "why-us" },
    { href: "/blog", label: "Blog", slug: "blog" },
    { href: "/#quote", label: "Get a Quote", slug: "quote" },
];

export const Header = () => {
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <header
            data-testid="site-header"
            className={`sticky top-0 z-40 bg-tp-forest text-white transition-shadow ${
                scrolled ? "shadow-[0_10px_24px_rgba(11,27,18,0.25)]" : ""
            }`}
        >
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 h-28 md:h-36 flex items-center justify-between gap-3">
                {/* Logo */}
                <a
                    href="#top"
                    data-testid="header-logo"
                    className="flex items-center gap-2 shrink-0"
                    aria-label={`${BUSINESS_NAME} home`}
                >
                    <img
                        src={LOGO_URL}
                        alt="Tipping Point Skips logo"
                        className="h-20 w-auto md:h-32 select-none"
                        draggable={false}
                    />
                    <span className="sr-only">{BUSINESS_NAME}</span>
                </a>

                {/* Desktop nav */}
                <nav
                    className="hidden md:flex items-center gap-1"
                    aria-label="Primary"
                >
                    {NAV.map((item) => (
                        <a
                            key={item.href}
                            href={item.href}
                            data-testid={`nav-${item.slug}`}
                            className="px-3 py-2 text-sm font-semibold text-white/90 hover:text-tp-gold rounded-md hover:bg-white/5 tp-focus transition-colors"
                        >
                            {item.label}
                        </a>
                    ))}
                </nav>

                {/* Desktop CTAs */}
                <div className="hidden md:flex items-center gap-2">
                    <a
                        href={PHONE_HREF}
                        data-testid="header-call-button"
                        className="tp-btn-primary text-sm tp-focus"
                    >
                        <Phone size={16} strokeWidth={2.5} />
                        <span>Call {PHONE_DISPLAY}</span>
                    </a>
                    <a
                        href="/#quote"
                        data-testid="header-quote-button"
                        className="tp-btn-outline text-sm tp-focus"
                    >
                        Get a Quote
                    </a>
                </div>

                {/* Mobile actions */}
                <div className="md:hidden flex items-center gap-2">
                    <a
                        href={PHONE_HREF}
                        data-testid="header-mobile-call"
                        aria-label="Call now"
                        className="inline-flex items-center gap-1.5 rounded-md bg-tp-gold text-tp-forest px-2.5 py-1.5 text-xs font-bold tp-focus"
                    >
                        <Phone size={14} strokeWidth={2.7} />
                        <span className="tabular-nums">{PHONE_DISPLAY}</span>
                    </a>
                    <Sheet>
                        <SheetTrigger asChild>
                            <button
                                data-testid="header-mobile-menu-button"
                                aria-label="Open menu"
                                className="p-2 -mr-1 rounded-md hover:bg-white/10 tp-focus"
                            >
                                <Menu size={22} strokeWidth={2.2} />
                            </button>
                        </SheetTrigger>
                        <SheetContent
                            side="right"
                            className="bg-tp-forest text-white border-none w-[86%] sm:w-[380px] p-0"
                        >
                            <SheetHeader className="px-5 py-4 border-b border-white/10 flex-row items-center justify-between">
                                <SheetTitle className="text-white font-heading text-lg">
                                    Menu
                                </SheetTitle>
                                <SheetClose asChild>
                                    <button
                                        aria-label="Close menu"
                                        className="p-2 rounded-md hover:bg-white/10"
                                    >
                                        <X size={20} />
                                    </button>
                                </SheetClose>
                            </SheetHeader>
                            <div className="px-3 py-4 flex flex-col gap-1">
                                {NAV.map((item) => (
                                    <SheetClose asChild key={item.href}>
                                        <a
                                            href={item.href}
                                            data-testid={`mobile-nav-${item.slug}`}
                                            className="px-3 py-3 text-base font-semibold text-white/95 hover:text-tp-gold hover:bg-white/5 rounded-md tp-focus transition-colors"
                                        >
                                            {item.label}
                                        </a>
                                    </SheetClose>
                                ))}
                                <div className="mt-4 flex flex-col gap-2 px-2">
                                    <a
                                        href={PHONE_HREF}
                                        data-testid="mobile-menu-call"
                                        className="tp-btn-primary justify-center tp-focus"
                                    >
                                        <Phone size={16} strokeWidth={2.5} />
                                        Call {PHONE_DISPLAY}
                                    </a>
                                    <SheetClose asChild>
                                        <a
                                            href="/#quote"
                                            data-testid="mobile-menu-quote"
                                            className="tp-btn-outline justify-center tp-focus"
                                        >
                                            Get a Quote
                                        </a>
                                    </SheetClose>
                                </div>
                            </div>
                        </SheetContent>
                    </Sheet>
                </div>
            </div>
        </header>
    );
};

export default Header;
